import React, { useEffect } from 'react'
import { StepWizardChildProps } from 'react-step-wizard'
import { useTranslation } from '../../../../i18n'
import { FormProvider, useForm } from 'react-hook-form'
import FButton from '../../../../components/BaseComponents/FButton'
import { FHeading1, FParagraph } from '../../../../components/BaseComponents/Text'
import SBox from '../../../../components/opinionless/SBox'
import FInput from '../../../../components/BaseComponents/FInput'
import { errors } from '../../../../components/BaseComponents/constants'

import FNeedHelpCTA from '../../../../components/BaseComponents/FNeedHelpCTA'
import { useBenefitsAnswerQueryQuery, useBenefitsAnswersMutation } from '../../../../types/graphql'
import FButtonPrevious from 'components/BaseComponents/FButton/FPreviousButton'
import { ga4_useTitle } from 'utils/ga4'
import { LBTextHeader, LBTextSubHeader } from 'containers/Apply/steps/ApplicationStepLBIntroduction'
import { useSTheme } from 'styles/theme'
import { WIZARD_STEPS_MAPPING } from 'containers/Apply/LongBeachWizard'
import { updatePercentageAndStep } from 'v2/components/XProgressBar/utils'

interface Props {
    personId?: any
    updatePercentage: any
}

const CalWorksWizardContainer: React.FC<Props> = (props: Props & StepWizardChildProps) => {
    const { t } = useTranslation(window.LANG_NS)
    const theme = useSTheme()
    const form = useForm()
    const { handleSubmit } = form

    const [saveBenefitAnswer, { loading }] = useBenefitsAnswersMutation()
    const { data, loading: queryLoading } = useBenefitsAnswerQueryQuery({
        variables: { personId: props.personId },
        fetchPolicy: 'network-only',
    })

    ga4_useTitle('CalWorks')

    const onFormSubmit = async (values) => {
        await saveBenefitAnswer({
            variables: {
                answers: [
                    {
                        personId: props.personId,
                        category: 'CAL_WORKS_AMOUNT',
                        answer: values.calworks_amount,
                    },
                ],
            },
        })
        props.nextStep()
    }

    useEffect(() => {
        if (data) {
            const savedValue = data.benefitsAnswers.find((answer: any) => answer.category === 'CAL_WORKS_AMOUNT')
                ?.answer
            form.setValue('calworks_amount', savedValue)
        }
    }, [data])

    const { percentage, wizardStep, childWizzardStep } = updatePercentageAndStep(
        WIZARD_STEPS_MAPPING.benefits,
        WIZARD_STEPS_MAPPING.benefits.calWorks,
        WIZARD_STEPS_MAPPING
    )
    props.updatePercentage(percentage, wizardStep, childWizzardStep)

    return (
        <React.Fragment>
            <FButtonPrevious onClick={props.previousStep} />
            <LBTextHeader
                sx={{
                    textAlign: 'left',
                    fontSize: '32px',
                    color: theme.colors.secondaryColor,
                    lineHeight: '120%',
                    fontFamily: theme.fonts.longBeach.body,
                    fontWeight: '900',
                }}
            >
                {t('calworks_heading')}
            </LBTextHeader>
            <LBTextSubHeader
                color={theme.colors.blackLabelfont}
                dangerouslySetInnerHTML={{
                    __html: t('calworks_p1'),
                }}
            />
            <FormProvider {...form}>
                <SBox as="form" onSubmit={handleSubmit(onFormSubmit)}>
                    <FInput
                        color={theme.colors.blackLabelfont}
                        labelColor={theme.colors.blackLabelFontAlpha}
                        id="calworks_amount"
                        name="calworks_amount"
                        label={t('calworks_amount_label')}
                        type="number"
                        placeholder={t('amount_in_dollars_placeholder')}
                        ref={form.register({
                            required: errors.default_required_key as string,
                            validate: {
                                positiveNumber: (value) => parseInt(value) >= 0 || 'positive_integer_validation',
                            },
                        })}
                    />
                    <FButton type="submit" loading={loading} ga4_key={t('continue', { lang: 'en' })}>
                        {t('save_and_continue')}
                    </FButton>
                </SBox>
            </FormProvider>
        </React.Fragment>
    )
}

export default CalWorksWizardContainer
